import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 lg:grid-cols-2" }
const _hoisted_4 = { class: "mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4" }
const _hoisted_5 = { class: "mb-2 text-lg font-semibold text-gray-800" }
const _hoisted_6 = { class: "mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4" }
const _hoisted_7 = { class: "mt-6 w-full" }
const _hoisted_8 = { class: "flex justify-end px-4 sm:px-0" }

import { computed, reactive, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { IntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FormAction,
    DefaultServiceResult,
    IntegrationServiceCreateResult,
    IntegrationType,
    MoskitIntegrationModel,
    MoskitIntegrationFormState,
    Permission,
} from '@/types';
import UiPanel from '@/components/UiPanel.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'MoskitIntegrationFormPage',
  props: {
    action: {
        type: String,
        default: FormAction.CREATE,
    },
},
  setup(__props) {

const uiStore = useUiStore();
const route = useRoute();

const props = __props;

const formState = reactive<MoskitIntegrationFormState>({
    type: IntegrationType.Moskit,
    token: null,
    activityIds: {
        BUSY: null,
        DITCHED: null,
        ANSWERED: null,
        NO_ANSWER: null,
    },
    customFields: {
        value: null,
        fieldId: null,
        fieldType: null,
    },
    isActive: true,
});

const rules = computed<ValidationArgs<MoskitIntegrationFormState>>(() => ({
    type: { required },
    token: { required, maxLength: maxLength(250) },
    activityIds: {
        BUSY: { required },
        DITCHED: { required },
        ANSWERED: { required },
        NO_ANSWER: { required },
    },
    customFields: {
        value: {},
        fieldId: {},
        fieldType: {},
    },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<MoskitIntegrationFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegration = async (id: number) => {
    await setLoading(true);
    try {
        const { data } = await IntegrationService.get<MoskitIntegrationModel>(id);
        // Map the API response to the form state
        formState.token = data.token;
        formState.activityIds.BUSY = data.activityIds.BUSY;
        formState.activityIds.DITCHED = data.activityIds.DITCHED;
        formState.activityIds.ANSWERED = data.activityIds.ANSWERED;
        formState.activityIds.NO_ANSWER = data.activityIds.NO_ANSWER;
        formState.customFields.value = data.customFields.value;
        formState.customFields.fieldId = data.customFields.fieldId;
        formState.customFields.fieldType = data.customFields.fieldType;
        formState.isActive = data.isActive;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    await setLoading(true);

    try {
        let response: AxiosResponse<DefaultServiceResult | IntegrationServiceCreateResult>;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await IntegrationService.update<DefaultServiceResult>(id, formState);
        } else {
            response = await IntegrationService.create<IntegrationServiceCreateResult>(formState);
        }

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ListIntegration' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        await getIntegration(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('core.entity.Integrations')) + " - " + _toDisplayString(_ctx.$t('integration.integrations.moskit.title')), 1)
      ]),
      _: 1
    }),
    _createElementVNode("form", {
      class: "space-y-4",
      autocomplete: "off",
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, [
      _createVNode(UiPanel, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).token.$model,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).token.$model) = $event)),
              name: "token",
              type: "text",
              label: _ctx.$t('integration.integrations.moskit.form.token'),
              errors: _unref(v$).token.$errors,
              onBlur: _unref(v$).token.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).activityIds.BUSY.$model,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).activityIds.BUSY.$model) = $event)),
              name: "activityIdsBusy",
              label: _ctx.$t('integration.integrations.moskit.form.activityIds.busy'),
              errors: _unref(v$).activityIds.BUSY.$errors,
              onBlur: _unref(v$).activityIds.BUSY.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).activityIds.DITCHED.$model,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).activityIds.DITCHED.$model) = $event)),
              name: "activityIdsDitched",
              label: _ctx.$t('integration.integrations.moskit.form.activityIds.ditched'),
              errors: _unref(v$).activityIds.DITCHED.$errors,
              onBlur: _unref(v$).activityIds.DITCHED.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).activityIds.ANSWERED.$model,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).activityIds.ANSWERED.$model) = $event)),
              name: "activityIdsAnswered",
              label: _ctx.$t('integration.integrations.moskit.form.activityIds.answered'),
              errors: _unref(v$).activityIds.ANSWERED.$errors,
              onBlur: _unref(v$).activityIds.ANSWERED.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).activityIds.NO_ANSWER.$model,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).activityIds.NO_ANSWER.$model) = $event)),
              name: "activityIdsNoAnswer",
              label: _ctx.$t('integration.integrations.moskit.form.activityIds.noAnswer'),
              errors: _unref(v$).activityIds.NO_ANSWER.$errors,
              onBlur: _unref(v$).activityIds.NO_ANSWER.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"])
          ])
        ]),
        _: 1
      }),
      _createVNode(UiPanel, null, {
        default: _withCtx(() => [
          _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t('integration.integrations.moskit.form.customFields.title')), 1),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).customFields.value.$model,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(v$).customFields.value.$model) = $event)),
              name: "customFieldsValue",
              label: _ctx.$t('integration.integrations.moskit.form.customFields.value'),
              errors: _unref(v$).customFields.value.$errors,
              onBlur: _unref(v$).customFields.value.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).customFields.fieldId.$model,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(v$).customFields.fieldId.$model) = $event)),
              name: "customFieldsFieldId",
              label: _ctx.$t('integration.integrations.moskit.form.customFields.fieldId'),
              errors: _unref(v$).customFields.fieldId.$errors,
              onBlur: _unref(v$).customFields.fieldId.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).customFields.fieldType.$model,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(v$).customFields.fieldType.$model) = $event)),
              name: "customFieldsFieldType",
              label: _ctx.$t('integration.integrations.moskit.form.customFields.fieldType'),
              errors: _unref(v$).customFields.fieldType.$errors,
              onBlur: _unref(v$).customFields.fieldType.$touch
            }, null, 8, ["modelValue", "label", "errors", "onBlur"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).isActive.$model,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
              name: "isActive",
              label: _ctx.$t('integration.integrations.moskit.form.isActive')
            }, null, 8, ["modelValue", "label"])
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_8, [
        _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListIntegration' } }),
        (_ctx.$can(_unref(Permission).EDIT_INTEGRATION))
          ? (_openBlock(), _createBlock(_component_UiButton, {
              key: 0,
              type: "submit",
              variant: "primary",
              "text-variant": "white",
              class: "ml-4"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ], 32),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ]))
}
}

})